import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import CountryFlag from './CountryFlag';
import { SUPPORTED_COUNTRIES } from '../utils/countryUtils';

const InternationalCountryModal = ({ isOpen, onClose, onSelectCountry }) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter countries based on search term
  const filteredCountries = SUPPORTED_COUNTRIES.filter(country =>
    country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    country.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    country.dialingCode.includes(searchTerm)
  );

  const handleCountrySelect = (country) => {
    onSelectCountry(country);
    onClose();
    setSearchTerm(''); // Reset search
  };

  const handleClose = () => {
    onClose();
    setSearchTerm(''); // Reset search
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="mx-auto max-w-md w-full">
            {/* Liquid Glass Panel */}
            <div 
              className="relative bg-black/20 backdrop-blur-2xl rounded-3xl border border-white/10 max-h-[85vh] flex flex-col"
              style={{
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 20px 40px rgba(0,0,0,0.3)'
              }}
            >
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-6 right-6 z-10 w-10 h-10 rounded-full bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-all"
              >
                <span className="text-lg">×</span>
              </button>

              {/* Header */}
              <div className="p-6 pb-4">
                <h2 className="text-xl font-medium text-white mb-2">
                  Select Country
                </h2>
                <p className="text-white/60 text-sm">
                  Choose the recipient's country to add the correct dialing code
                </p>
              </div>

              {/* Search */}
              <div className="px-6 pb-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-white/40" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search countries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm text-white placeholder-white/40 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 focus:bg-white/10 sm:text-sm transition-all duration-200"
                  />
                </div>
              </div>

              {/* Countries List */}
              <div className="flex-1 overflow-y-auto px-6 pb-6">
                <div className="space-y-2">
                  {filteredCountries.length === 0 ? (
                    <div className="text-center py-8 text-white/60">
                      No countries found
                    </div>
                  ) : (
                    filteredCountries.map((country, index) => (
                      <button
                        key={country.code}
                        onClick={() => handleCountrySelect(country)}
                        className="w-full p-3 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm text-white hover:bg-white/10 hover:border-white/20 transition-all duration-200 flex items-center gap-3"
                        style={{
                          animation: `slideInUp 0.2s ease-out ${Math.min(index * 0.05, 0.5)}s both`
                        }}
                      >
                        <div className="flex-shrink-0">
                          <CountryFlag countryCode={country.code} size="md" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-medium text-sm">
                            {country.name}
                          </div>
                          <div className="text-xs text-white/60">
                            {country.dialingCode}
                          </div>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes slideInUp {
          from { 
            opacity: 0; 
            transform: translateY(10px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
      `}</style>
    </Dialog>
  );
};

InternationalCountryModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectCountry: PropTypes.func.isRequired
};

export default InternationalCountryModal; 